import { lazy, Suspense, useCallback, useState } from 'react';

import { useSearchParams } from 'react-router';

import { Box } from '@/shared/ui';

import {
  FWDCURVE_CATALOG_PATH,
  SHOW_SELECTED_URL_PARAM,
  SPOT_CATALOG_PATH
} from '@/constants/constants';
import { Z_INDEX } from '@/constants/z-index';
// TODO: import from '@/features/dashboard' once Dashboard feature has public index.ts
import { useAddToAppContext } from '@/features/Dashboard/context/AppContext';
// TODO: import from '@/features/dashboard' once Dashboard feature has public index.ts
import { useDashboardEditLockContext } from '@/features/Dashboard/context/DashboardEditLockContext';
// TODO: import from '@/features/factsheet' once Factsheet feature has public index.ts
import { FACTSHEET_PARAM_NAME } from '@/features/Factsheet/constants';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { useDrawerBackButton } from '@/hooks/useDrawerBackButton';
import useOpenExportSelection, { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
// TODO: move to store/slices/ and update once drawerSlice is migrated to a feature
import { closeDrawer, DrawerType, openDrawer } from '@/slices/drawerSlice';

import { useResolveForwardCurveWildcards } from '../hooks/useResolveForwardCurveWildcards';
import { useSymbolSelector } from '../hooks/useSymbolSelector';
import { clearSelection, SELECTOR_MODES } from '../state/symbolSelector.slice';
import LimitDialog from './LimitDialog/LimitDialog';
import { SymbolSelectorToolbar } from './SymbolSelectorToolbar';
// TODO: import from '@/features/data-hub' once DataHub feature has public index.ts
const ExportDrawer = lazy(() => import('@/features/DataHub/components/ExportDrawer'));

const SymbolSelector = () => {
  useResolveForwardCurveWildcards();

  const dispatch = useAppDispatch();
  const symbolSelectorState = useAppSelector(state => state.symbolSelector);
  const openDrawerType = useAppSelector(state => state.drawer.openDrawer);
  const { onSubmit } = useAddToAppContext();
  const { requestExclusiveAction } = useDashboardEditLockContext();
  const { handleDrag, handleClose, position, navigate, isInCatalog, isDashboardAppSelector } =
    useSymbolSelector();

  const [, setUrlSearchParams] = useSearchParams();
  const [dragging, setDragging] = useState(false);
  const openExportSelection = useOpenExportSelection(ExportSelectionSource.SYMBOL_SELECTOR);

  const isExportOpen = openDrawerType === DrawerType.EXPORT;
  const closeDrawerOnly = useCallback(() => dispatch(closeDrawer()), [dispatch]);
  const openExportDrawer = useCallback(() => dispatch(openDrawer(DrawerType.EXPORT)), [dispatch]);

  useDrawerBackButton(isExportOpen, closeDrawerOnly, openExportDrawer);

  const deleteFactsheetParam = () => {
    setUrlSearchParams(searchParams => {
      searchParams.delete(FACTSHEET_PARAM_NAME);
      return searchParams;
    });
  };

  const handleViewCatalog = () => {
    deleteFactsheetParam();
    const catalogPath =
      symbolSelectorState.selectorMode === SELECTOR_MODES.FORWARD_CURVE
        ? FWDCURVE_CATALOG_PATH
        : SPOT_CATALOG_PATH;
    navigate(`/catalog/${catalogPath}?${SHOW_SELECTED_URL_PARAM}=true`);
  };

  const handleExport = () => {
    deleteFactsheetParam();
    openExportSelection();
  };

  const isVisible =
    symbolSelectorState.symbols.length > 0 && !symbolSelectorState.limitReached && !isExportOpen;

  return (
    <>
      <LimitDialog
        handleClose={handleClose}
        open={symbolSelectorState.limitReached}
        selectorMode={symbolSelectorState.selectorMode}
      />
      {dragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: Z_INDEX.priorityMid,
            pointerEvents: 'all'
          }}
        />
      )}
      {position && (
        <SymbolSelectorToolbar
          isDashboardAppSelector={isDashboardAppSelector}
          isInCatalog={isInCatalog}
          isVisible={isVisible}
          position={position}
          selectorMode={symbolSelectorState.selectorMode}
          symbols={symbolSelectorState.symbols}
          onAddToApp={() => onSubmit?.(symbolSelectorState.symbols)}
          onClear={() => dispatch(clearSelection())}
          onDrag={handleDrag}
          onDraggingChange={setDragging}
          onExport={handleExport}
          onRequestExclusiveAction={requestExclusiveAction}
          onViewCatalog={handleViewCatalog}
        />
      )}
      <Suspense fallback={null}>
        <ExportDrawer
          open={isExportOpen}
          onClose={closeDrawerOnly}
        />
      </Suspense>
    </>
  );
};

export default SymbolSelector;
